<script setup>
import { ref, watch, onMounted } from 'vue'
import dayjs from 'dayjs'
import SectionTitle from './SectionTitle.vue'
import Toggle from './Toggle.vue'
import { useAmedasRank } from '../composable/useAmedasRank.js'
import { useCommon } from '../composable/useCommon.js'

const props = defineProps({
  elmType: {
    type: String,
    default: '気温'
  },
  area: {
    type: String,
    default: ''
  },
  areaRoma: {
    type: String,
    default: ''
  },
  pref: {
    type: String,
    default: ''
  },
  prefRoma: {
    type: String,
    default: ''
  },
  devDomains: {
    type: Object,
    default: () => {}
  },
  appViewShow: {
    type: Boolean,
    default: true
  },
  isWebViewProp: {
    type: Boolean,
    default: false
  }
})

const { isWebView } = useCommon()
const isWebViewFlag = ref(props.isWebViewProp)

const { getAmedasRank, getRankingLink } = useAmedasRank(props.devDomains)
const title = ref('アメダスランキング')
const head = ['順位', '地点', '観測値']
const ranking = ref([{}, {}, {}])
const selectedType = ref('気温')
const month = dayjs().month() + 1
const toggleList = (month >= 10 || month + 1 <= 5) ? ['気温', '降水量', '風', '降雪量', '湿度'] : ['気温', '降水量', '風', '湿度']
const isError = ref(false)

async function getData (params) {
  if (props.pref && props.prefRoma) {
    ranking.value = await getAmedasRank({ id: params, length: 3, pref: props.prefRoma })
  } else if (props.area && props.areaRoma) {
    ranking.value = await getAmedasRank({ id: params, length: 3, area: props.areaRoma })
  } else {
    ranking.value = await getAmedasRank({ id: params, length: 3 })
  }
  if (ranking.value.length === 0) isError.value = true
}

watch(selectedType, (value) => {
  getData(value)
})

getData(selectedType.value)
if (props.pref && props.prefRoma) {
  title.value = `${props.pref}のアメダス`
} else if (props.area && props.areaRoma) {
  title.value = `${props.area}のアメダス`
}

onMounted(() => {
  isWebViewFlag.value = isWebView()
  selectedType.value = props.elmType
})
</script>

<template>
  <section
    v-if="(!isWebViewFlag || appViewShow) && !isError"
    class="amedas"
  >
    <SectionTitle
      :title="title"
      :button-link="getRankingLink({ id: selectedType, area: props.areaRoma, pref: props.prefRoma })"
    />
    <Toggle
      v-model:selected="selectedType"
      :list="toggleList"
      form="square"
    />
    <ul class="ranking">
      <li>
        <div
          class="row"
        >
          <p
            v-for="text in head"
            :key="text"
            class="text"
          >
            {{ text }}
          </p>
        </div>
        <div
          v-for="item in ranking"
          :key="item.area"
          class="list"
        >
          <p class="rank">
            {{ item.rank }}
          </p>
          <div class="item">
            <a
              class="area"
              :href="item.href"
            >{{ item.area }}</a>
            <p class="note">
              <span class="only-main">(</span>{{ item.region }}<span class="only-main">)</span>
            </p>
          </div>
          <div class="item">
            <p class="value">
              {{ item.value }}
            </p>
            <p class="note">
              <span class="only-main">(</span>{{ item.date }}<span class="only-main">)</span>
            </p>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>

<style lang="scss" scoped>
.amedas {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .row {
    display: flex;
    padding-top: 5px;
    padding-bottom: 5px;
    background-color: #eef1f8;
    border-top: solid 1px #c6ccdc;
    border-bottom: solid 1px #c6ccdc;
    .text {
      color: #0c419a;
      font-size: 13px;
      font-weight: bold;
      line-height: 13px;
      padding-left: 5px;
      padding-right: 5px;
      &:first-child {
        width: 40px;
        text-align: center;
      }
      &:nth-child(2) {
        width: calc(50% - 20px);
      }
      &:last-child {
        width: calc(50% - 20px);
        text-align: right;
      }
    }
  }
  .ranking {
    display: flex;
    flex-direction: column;
    .list {
      height: 37px;
      display: flex;
      align-items: center;
      padding-top: 4px;
      padding-bottom: 4px;
      border-bottom: solid 1px #e5edf0;
    }
    .rank {
      width: 40px;
      color: #303030;
      font-size: 13px;
      font-weight: bold;
      line-height: 13px;
      text-align: center;
    }
    .item {
      display: flex;
      flex-direction: column-reverse;
      width: calc(50% - 20px);
      padding-left: 5px;
      padding-right: 5px;
      &:last-child {
        align-items: flex-end;
      }
    }
    .area {
      color: #3569c0;
      font-size: 15px;
      font-weight: bold;
      line-height: 15px;
    }
    .note {
      display: flex;
      color: #303030;
      font-size: 10px;
      line-height: 13px;
    }
    .value {
      color: #303030;
      font-size: 15px;
      font-weight: bold;
      line-height: 15px;
    }
  }
  .only-main {
    display: none;
  }
  @media screen and (min-width: 830px) {
    .ranking {
      .area {
        transition: opacity .2s ease-in-out;
        &:hover {
          opacity: .6;
        }
      }
    }
  }
}
.main {
  @media screen and (min-width: 830px) {
    .ranking {
      .list {
        padding-top: 8px;
        padding-bottom: 8px;
      }
      .item {
        flex-direction: row;
        align-items: center;
        gap: 2px;
        &:last-child {
          align-items: center;
          justify-content: end;
        }
        .only-main {
          display: block;
        }
      }
    }
  }
}
</style>
